/**
 * Empty State component.
 * Premium empty state with gradient icon and modern CTA button. CoOrdio DS icons (Lucide).
 */

import type { ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Inbox } from '@core/components/icons';

/**
 * Empty State component props
 */
export interface EmptyStateProps {
  title?: string;
  description?: string;
  icon?: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  height?: number | string;
}

/**
 * Empty State component with modern glassmorphism design.
 */
const EmptyState = ({
  title = 'No data available',
  description = 'There is no data to display at this time.',
  icon,
  actionLabel,
  onAction,
  height = 400,
}: EmptyStateProps) => {
  const iconContent = icon ?? <Inbox size={32} />;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: height,
        p: 6,
        textAlign: 'center',
        borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
        border: '1px solid var(--glass-border)',
        background: 'var(--glass-bg-subtle)',
      }}
    >
      {/* Gradient Icon Container */}
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: 'var(--mui-shape-customBorderRadius-xl)',
          background: 'var(--gradient-primary-subtle)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 4,
          animation: 'float 3s ease-in-out infinite',
        }}
      >
        <Box
          sx={{
            fontSize: '2rem',
            color: 'var(--mui-palette-primary-main)',
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {iconContent}
        </Box>
      </Box>

      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: actionLabel ? 5 : 0, maxWidth: 420, lineHeight: 1.6 }}
      >
        {description}
      </Typography>
      {actionLabel && onAction && (
        <Button
          variant="contained"
          color="primary"
          onClick={onAction}
          startIcon={<AddIcon />}
          sx={{ px: 5 }}
        >
          {actionLabel}
        </Button>
      )}
    </Box>
  );
};

export default EmptyState;
